import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft } from "lucide-react";
import { useHideEmergentBadge } from "@/hooks/useHideEmergentBadge";

export function LegalShell({ title, eyebrow, children }) {
  useHideEmergentBadge();
  return (
    <div className="min-h-screen bg-[#f9f9fb] text-[#0a0a0a]">
      <header className="border-b border-[#e5e5eb] bg-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-[#0a0a0a] hover:text-[#002fa7]">
            <Sparkles className="w-4 h-4 text-[#002fa7]" />
            <span className="font-display text-base font-extrabold tracking-tight">SparkLoop</span>
          </Link>
          <Link to="/" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5a5a65] inline-flex items-center gap-1.5 hover:text-[#0a0a0a]">
            <ArrowLeft className="w-3 h-3" /> Home
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
        <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-3">
          {eyebrow}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[1.05] mb-8">
          {title}
        </h1>
        <article className="prose prose-sm max-w-none text-[#0a0a0a] leading-relaxed space-y-5">
          {children}
        </article>
        <footer className="mt-16 pt-6 border-t border-[#e5e5eb] text-xs text-[#5a5a65] flex flex-wrap gap-4">
          <Link to="/privacy" className="hover:text-[#0a0a0a]">Privacy</Link>
          <Link to="/terms" className="hover:text-[#0a0a0a]">Terms</Link>
          <Link to="/contact" className="hover:text-[#0a0a0a]">Contact</Link>
          <span className="ml-auto">© SparkLoop Rewards</span>
        </footer>
      </main>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell eyebrow="Legal" title="Privacy policy">
      <p className="text-[#5a5a65] text-xs">Last updated: May 2026</p>

      <h2 className="font-display text-xl font-extrabold mt-6">Who we are</h2>
      <p>
        SparkLoop Rewards ("SparkLoop", "we", "us") provides a points-based
        loyalty program platform that businesses use to reward their customers.
        This policy explains what information we collect, how we use it, and
        the choices available to you.
      </p>

      <h2 className="font-display text-xl font-extrabold mt-6">Information we collect</h2>
      <ul className="list-disc pl-5 space-y-1.5">
        <li><span className="font-semibold">Merchant account data:</span> business name, owner name, username, email (when provided), and a password hash. Used to authenticate you and operate your loyalty program.</li>
        <li><span className="font-semibold">Customer identifiers:</span> phone number and (optional) first name entered at the in-store kiosk. Used solely to identify the same customer across visits so points accrue correctly.</li>
        <li><span className="font-semibold">Activity data:</span> visits, purchase amounts, points awarded and redeemed at participating merchants.</li>
        <li><span className="font-semibold">Billing data:</span> handled by Stripe, Inc. We never see or store card numbers. We retain a Stripe subscription identifier and the most recent payment status.</li>
        <li><span className="font-semibold">Support messages:</span> name, email, business, and any message you send through the in-app chat or contact form.</li>
      </ul>

      <h2 className="font-display text-xl font-extrabold mt-6">How we use it</h2>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>To operate the loyalty program (authenticate merchants, track customer balances, issue rewards).</li>
        <li>To process subscription payments via Stripe.</li>
        <li>To respond to support requests.</li>
        <li>To detect abuse and protect the service.</li>
      </ul>
      <p>We do not sell personal information. We do not run third-party advertising on the platform.</p>

      <h2 className="font-display text-xl font-extrabold mt-6">Third parties</h2>
      <ul className="list-disc pl-5 space-y-1.5">
        <li><span className="font-semibold">Stripe:</span> processes all payments. Subject to Stripe's privacy policy.</li>
        <li><span className="font-semibold">Hosting:</span> our application and database are hosted by industry-standard cloud providers in the United States.</li>
        <li><span className="font-semibold">AI support chat:</span> messages sent through the in-app chat may be processed by an LLM provider strictly to generate a response. No payment data is shared.</li>
      </ul>

      <h2 className="font-display text-xl font-extrabold mt-6">Data retention</h2>
      <p>We keep merchant and customer records for as long as the merchant maintains an active SparkLoop account, plus a reasonable period for tax and dispute purposes. You can request deletion at any time by emailing the address below.</p>

      <h2 className="font-display text-xl font-extrabold mt-6">Your rights</h2>
      <p>Depending on where you live, you may have the right to access, correct, export, or delete your personal data. To exercise these rights, contact us at <a href="mailto:sparklooployalty@gmail.com" className="text-[#002fa7] underline">sparklooployalty@gmail.com</a>.</p>

      <h2 className="font-display text-xl font-extrabold mt-6">Changes</h2>
      <p>If we materially change this policy, we will update the "Last updated" date and notify active merchant accounts.</p>

      <h2 className="font-display text-xl font-extrabold mt-6">Contact</h2>
      <p>Email <a href="mailto:sparklooployalty@gmail.com" className="text-[#002fa7] underline">sparklooployalty@gmail.com</a>.</p>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell eyebrow="Legal" title="Terms of service">
      <p className="text-[#5a5a65] text-xs">Last updated: May 2026</p>

      <p>By creating a SparkLoop merchant account or using the SparkLoop kiosk in a participating business, you agree to these terms.</p>

      <h2 className="font-display text-xl font-extrabold mt-6">The service</h2>
      <p>SparkLoop provides software to run a points-based loyalty program. Merchants subscribe; their customers earn and redeem points at the merchant's location. SparkLoop is the software provider — the rewards themselves are issued by the merchant.</p>

      <h2 className="font-display text-xl font-extrabold mt-6">Accounts</h2>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>You are responsible for keeping your merchant credentials secure.</li>
        <li>You may not share, resell, or assign your account.</li>
        <li>You must provide accurate business information.</li>
      </ul>

      <h2 className="font-display text-xl font-extrabold mt-6">Subscriptions and billing</h2>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Subscriptions are billed in advance via Stripe at the rate displayed at signup.</li>
        <li>Plans renew automatically until cancelled.</li>
        <li>Refunds are issued at our discretion; contact support for any billing dispute.</li>
        <li>If a payment fails, access to merchant features may be paused until the balance is cleared.</li>
      </ul>

      <h2 className="font-display text-xl font-extrabold mt-6">Acceptable use</h2>
      <p>You agree not to use SparkLoop to operate a deceptive loyalty program, to spam customers, to attempt to gain unauthorized access, or to violate any law.</p>

      <h2 className="font-display text-xl font-extrabold mt-6">Termination</h2>
      <p>You may cancel at any time. We may suspend or terminate accounts that violate these terms, with notice when reasonable.</p>

      <h2 className="font-display text-xl font-extrabold mt-6">Disclaimer & limitation of liability</h2>
      <p>The service is provided "as is". To the maximum extent permitted by law, SparkLoop's aggregate liability is limited to the amounts you paid us in the twelve months before the claim.</p>

      <h2 className="font-display text-xl font-extrabold mt-6">Changes</h2>
      <p>We may update these terms; continued use after a change constitutes acceptance.</p>

      <h2 className="font-display text-xl font-extrabold mt-6">Contact</h2>
      <p>Email <a href="mailto:sparklooployalty@gmail.com" className="text-[#002fa7] underline">sparklooployalty@gmail.com</a>.</p>
    </LegalShell>
  );
}

export function ContactPage() {
  return (
    <LegalShell eyebrow="Get in touch" title="Contact us">
      <p>We respond to most inquiries within one business day.</p>
      <h2 className="font-display text-xl font-extrabold mt-6">Email</h2>
      <p><a href="mailto:sparklooployalty@gmail.com" className="text-[#002fa7] underline" data-testid="contact-email">sparklooployalty@gmail.com</a></p>
      <h2 className="font-display text-xl font-extrabold mt-6">Support topics</h2>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Setting up a new business / kiosk</li>
        <li>Billing &amp; subscription questions</li>
        <li>Customer data requests</li>
        <li>Press, partnerships, or general inquiries</li>
      </ul>
      <p className="mt-6 text-[#5a5a65] text-sm">If you're already a SparkLoop merchant, the support chat inside your dashboard is the fastest way to reach us.</p>
    </LegalShell>
  );
}
